import { Tooltip, UnstyledButton } from "@mantine/core";
import { Image as ImageIcon, Mic, SquarePlus, X } from "lucide-react";

import { UNSUPPORTED } from "../../../platform/media/ImageRefsSlot";
import slots from "../../../platform/media/slots.module.css";
import type { ElementDraft } from "./elements";
import classes from "./elements.module.css";
import { SoonSlot } from "./SoonSlot";

export interface ElementsProps {
  elements: ElementDraft[];
  /** Live's "@Element1" chip on each, where the model tags elements. */
  tagged: boolean;
  /** The inputs chosen leave no mode that takes elements: live greys the tile. */
  disabled: boolean;
  /** At the model's most elements the tile greys out, with no message (live). */
  full: boolean;
  /** The name of the voice the element at this place speaks with, or null. */
  voiceOf: (at: number) => string | null;
  onCreate: () => void;
  onEdit: (at: number) => void;
  onRemove: (at: number) => void;
}

/**
 * Live's Elements (#229, LEFTOVERS 2): each element made as a mosaic of its pictures with live's
 * x and, where the model tags elements, its "@Element1" chip; a click opens it again. The tile
 * opens "Create Element". Without `elements` (no mode of the model takes any) the tile is
 * Coming soon, in live's place.
 */
export function ElementsSlot({ elements }: { elements: ElementsProps | undefined }) {
  if (elements === undefined) {
    return <SoonSlot label="Elements" icon={<SquarePlus size={20} strokeWidth={1.5} />} />;
  }
  const tile = (
    <UnstyledButton
      className={slots.slot}
      aria-label="Elements"
      disabled={elements.disabled || elements.full}
      onClick={elements.onCreate}
    >
      <SquarePlus size={20} strokeWidth={1.5} aria-hidden />
      <span className={slots.slotLabel}>Elements</span>
    </UnstyledButton>
  );
  return (
    <>
      {elements.elements.map((element, at) => {
        const pictures = [element.frontal, ...element.images, element.video].filter(
          (each) => each !== null,
        );
        const voice = elements.voiceOf(at);
        return (
          // Elements have no identity of their own: their place names them (@Element1).
          <span key={at} className={slots.slotFilled}>
            <UnstyledButton
              className={classes.mosaic}
              data-rows={pictures.length > 2 ? 2 : 1}
              aria-label={voice === null ? `Element ${at + 1}` : `Element ${at + 1}: ${voice}`}
              onClick={() => elements.onEdit(at)}
            >
              {pictures.slice(0, 4).map((picture) =>
                picture.url === null ? (
                  <span key={picture.assetId} className={classes.mosaicCell}>
                    <ImageIcon size={14} aria-hidden />
                  </span>
                ) : (
                  <img key={picture.assetId} src={picture.url} alt="" className={classes.mosaicCell} />
                ),
              )}
              {voice === null ? null : (
                <span className={classes.mosaicVoice}>
                  <Mic size={10} strokeWidth={2} aria-hidden />
                  <span className={classes.mosaicVoiceName}>{voice}</span>
                </span>
              )}
              {elements.tagged ? <span className={slots.tag}>@Element{at + 1}</span> : null}
            </UnstyledButton>
            <UnstyledButton
              className={slots.thumbRemove}
              aria-label={`Remove element ${at + 1}`}
              onClick={() => elements.onRemove(at)}
            >
              <X size={14} strokeWidth={2.5} aria-hidden />
            </UnstyledButton>
          </span>
        );
      })}
      {elements.disabled ? (
        <Tooltip label={UNSUPPORTED} withArrow>
          <span className={slots.slotFilled}>{tile}</span>
        </Tooltip>
      ) : (
        tile
      )}
    </>
  );
}
